/**
 * AD-27's alphabets, and the arithmetic that makes the check symbol worth having.
 *
 * A pass code is twenty Crockford base32 data symbols, about a hundred bits, plus a twenty-first
 * CHECK symbol drawn from Crockford's 37-symbol check alphabet. The five extra glyphs are what
 * make the modulus prime, and the primality is what turns "probably catches typos" into "catches
 * every typo of these two shapes".
 *
 * **The proof, kept beside the code because a later simplification would break it silently.**
 * Let `N` be the base-32 value of the twenty data symbols and let `check = N mod 37`.
 *
 * - A **single-symbol substitution** changes `N` by `delta * 32^k` with `0 < |delta| <= 31 < 37`.
 *   Since 37 is prime and `gcd(32, 37) = 1`, neither factor is `0 mod 37`, so the product is not
 *   either: the check symbol always moves. Every single-symbol corruption is detected.
 * - **Swapping two adjacent data symbols** changes `N` by `(d[i+1] - d[i]) * 32^(18-i) * 31`.
 *   None of the three factors is `0 mod 37` unless the two symbols were equal, which is not an
 *   error but the same code. Every adjacent transposition is detected.
 * - **Swapping the last data symbol with the check symbol** is covered by the same argument.
 *   Either the check symbol is one of the five extra glyphs, in which case the swap puts a
 *   non-data glyph into the data and fails the alphabet test outright; or it is a data glyph `c`,
 *   and acceptance would require `N + c - d19 == d19 (mod 37)` while `N == c (mod 37)`, hence
 *   `2c == 2*d19`, hence `c == d19` because 2 is invertible modulo 37. That is the no-op swap.
 *
 * **A modulus of 36 or 32 breaks both arguments**, because 32 and 36 share a factor and 32 divides
 * 32. The property tests are the only thing that would notice, which is exactly why this note is
 * here: trimming the check alphabet back to 32 symbols to make every code QR-alphanumeric looks
 * like a tidy-up and is a silent downgrade of the detection guarantee.
 *
 * **Two of the five extra glyphs (`~` and `=`) are outside QR alphanumeric mode**, so a small
 * fraction of codes encode in byte mode instead. At error-correction level `high` that is fine and
 * it must not be "fixed".
 *
 * **AD-61 answers that in the one place it can be answered without weakening anything: the
 * ISSUER.** `pass/mint.ts` draws through `generatePassCode` and redraws when the check symbol is
 * one of `*~$=`, so every code this facility prints is twenty-one characters of `[0-9A-Z]`. That
 * is emphatically not the trim this note warns against: the modulus stays 37, the alphabet keeps
 * all 37 symbols, `checkSymbolFor` and `parsePassCode` are untouched, and the verifier still
 * accepts a code whose check symbol is `~`. The detection guarantee is a statement about the
 * verifier, and it is intact.
 */
/** Crockford base32: the digits and the letters, with I, L, O and U left out. */
export const CROCKFORD_DATA_ALPHABET = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';
/** The data alphabet plus Crockford's five check glyphs, 37 symbols in total. */
export const CROCKFORD_CHECK_ALPHABET = `${CROCKFORD_DATA_ALPHABET}*~$=U`;
/** Prime, which is what the detection proof above rests on. */
export const CHECK_MODULUS = 37;
/** Twenty data symbols, about 100 bits of entropy. */
export const DATA_SYMBOLS = 20;
/** Twenty data symbols plus one check symbol. */
export const TOTAL_SYMBOLS = 21;
/** Displayed as three groups of seven, which is what makes a code readable aloud. */
export const GROUP_SIZE = 7;
/** The hyphen between groups, and the character a parser therefore has to ignore. */
export const GROUP_SEPARATOR = '-';
function indexTable(alphabet) {
    const table = new Map();
    for (const [index, symbol] of Array.from(alphabet).entries()) {
        table.set(symbol, index);
    }
    return table;
}
export const DATA_VALUE = indexTable(CROCKFORD_DATA_ALPHABET);
export const CHECK_VALUE = indexTable(CROCKFORD_CHECK_ALPHABET);
/**
 * The confusable glyphs an operator may type or read aloud.
 *
 * `U` is deliberately NOT folded. Crockford folds it to V in some presentations; here it is a
 * legitimate CHECK symbol, and folding it would make one code in thirty-seven unparseable.
 */
export const INPUT_ALIASES = new Map([
    ['O', '0'],
    ['I', '1'],
    ['L', '1'],
]);
//# sourceMappingURL=alphabet.js.map